import React, { useState, useEffect } from 'react';
import {
  Newspaper, Send, Mail, MessageSquare, Clock, Calendar, 
  Sparkles, CheckCircle2, AlertCircle, RefreshCw, Trash2, 
  Play, Pause, ExternalLink, Copy, Check, Download, ArrowLeft,
  FileText, Zap, Settings, History, HelpCircle
} from 'lucide-react';

interface NewsAnalystProps {
  user: any;
  language: 'uk' | 'en' | 'pl';
  onBackToHub: () => void;
  onGoHome?: () => void;
}

interface NewsMonitor {
  id: string;
  topic: string;
  frequency: 'hourly' | 'daily' | 'weekly' | 'workdays';
  execution_time: string;
  delivery_channel: 'email' | 'telegram' | 'both';
  target_email?: string;
  telegram_chat_id?: string;
  is_active: boolean;
  last_run_at?: string;
  created_at: string;
}

interface NewsReport {
  id: string;
  topic: string;
  report_title: string;
  report_content: string;
  delivery_channel: string;
  delivery_status: string;
  sent_at: string;
}

const TOPIC_SUGGESTIONS = [
  { uk: "Штучний інтелект & Нейромережі", en: "Artificial Intelligence & Neural Nets" },
  { uk: "Податкові та юридичні новини України", en: "Tax & Legal News of Ukraine" },
  { uk: "Крипто-ринок, Bitcoin & Web3", en: "Crypto Market, Bitcoin & Web3" },
  { uk: "Державні закупівлі Prozorro & Тендери", en: "Prozorro Public Procurement & Tenders" },
  { uk: "Макроекономіка & Фінанси України", en: "Macroeconomics & Ukrainian Finance" },
  { uk: "Ринок нерухомості Києва та України", en: "Real Estate Market in Ukraine" }
];

export default function NewsAnalyst({ user, language, onBackToHub, onGoHome }: NewsAnalystProps) {
  const isUk = language === 'uk';
  const [activeSubTab, setActiveSubTab] = useState<'settings' | 'instant' | 'history'>('settings');

  // Form State for Monitor Settings
  const [topic, setTopic] = useState('');
  const [frequency, setFrequency] = useState<'hourly' | 'daily' | 'weekly' | 'workdays'>('daily');
  const [executionTime, setExecutionTime] = useState('09:00');
  const [deliveryChannel, setDeliveryChannel] = useState<'email' | 'telegram' | 'both'>('telegram');
  const [targetEmail, setTargetEmail] = useState(user?.email || '');
  const [telegramChatId, setTelegramChatId] = useState('');
  const [editingMonitorId, setEditingMonitorId] = useState<string | null>(null);
  const [isPublishingSocial, setIsPublishingSocial] = useState(false);

  // Instant Search State
  const [instantTopic, setInstantTopic] = useState('');
  const [instantSendNow, setInstantSendNow] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [currentReport, setCurrentReport] = useState<{ title: string; content: string; deliveryStatus?: string } | null>(null);
  const [copied, setCopied] = useState(false);

  // Data Lists State
  const [monitors, setMonitors] = useState<NewsMonitor[]>([]);
  const [reports, setReports] = useState<NewsReport[]>([]);
  const [isLoadingMonitors, setIsLoadingMonitors] = useState(false);
  const [isLoadingReports, setIsLoadingReports] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [runningMonitorId, setRunningMonitorId] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [selectedHistoryReport, setSelectedHistoryReport] = useState<NewsReport | null>(null);

  // Load monitors and report history
  const fetchMonitors = async () => {
    setIsLoadingMonitors(true);
    try {
      const res = await fetch('/api/news-monitors');
      if (res.ok) {
        const data = await res.json();
        setMonitors(data.monitors || []);
      }
    } catch (e) {
      console.error("Error fetching monitors:", e);
    } finally {
      setIsLoadingMonitors(false);
    }
  };

  const fetchReports = async () => {
    setIsLoadingReports(true);
    try {
      const res = await fetch('/api/news-reports');
      if (res.ok) {
        const data = await res.json();
        setReports(data.reports || []);
      }
    } catch (e) {
      console.error("Error fetching reports:", e);
    } finally {
      setIsLoadingReports(false);
    }
  };

  useEffect(() => {
    fetchMonitors();
    fetchReports();
  }, []);

  // Save monitor settings
  const handleSaveMonitor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!topic.trim()) {
      setStatusMessage({ type: 'error', text: isUk ? 'Вкажіть тему для аналізу новин.' : 'Please enter a topic for news analysis.' });
      return;
    }

    setIsSaving(true);
    setStatusMessage(null);

    try {
      const payload = {
        topic: topic.trim(),
        frequency,
        executionTime,
        deliveryChannel,
        targetEmail: targetEmail.trim() || user?.email,
        telegramChatId: telegramChatId.trim() || null,
        isActive: true
      };

      let res;
      if (editingMonitorId) {
        res = await fetch(`/api/news-monitors/${editingMonitorId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        res = await fetch('/api/news-monitors', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (res.ok) {
        setStatusMessage({
          type: 'success',
          text: editingMonitorId
            ? (isUk ? 'Налаштування моніторингу успішно оновлено! ⚙️' : 'Monitor settings updated successfully!')
            : (isUk ? 'Новий моніторинг новин успішно активовано! 🚀' : 'New news monitor activated!')
        });
        setTopic('');
        setEditingMonitorId(null);
        fetchMonitors();
      } else {
        const data = await res.json();
        throw new Error(data.error || 'Failed to save settings');
      }
    } catch (err: any) {
      setStatusMessage({ type: 'error', text: err.message });
    } finally {
      setIsSaving(false);
    }
  };

  // Toggle active status
  const handleToggleActive = async (monitor: NewsMonitor) => {
    try {
      const res = await fetch(`/api/news-monitors/${monitor.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !monitor.is_active })
      });
      if (res.ok) {
        fetchMonitors();
      }
    } catch (e) {
      console.error("Error toggling monitor active status:", e);
    }
  };

  // Delete monitor
  const handleDeleteMonitor = async (id: string) => {
    if (!confirm(isUk ? "Ви дійсно бажаєте видалити цей моніторинг?" : "Are you sure you want to delete this monitor?")) return;
    try {
      const res = await fetch(`/api/news-monitors/${id}`, { method: 'DELETE' });
      if (res.ok) {
        fetchMonitors();
      }
    } catch (e) {
      console.error("Error deleting monitor:", e);
    }
  };

  // Run monitor immediately
  const handleRunMonitorNow = async (monitor: NewsMonitor) => {
    setRunningMonitorId(monitor.id);
    setStatusMessage(null);
    try {
      const res = await fetch(`/api/news-monitors/${monitor.id}/run`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        setStatusMessage({
          type: 'success',
          text: isUk 
            ? `Аналіз новин за темою "${monitor.topic}" завершено та надіслано! (${data.deliveryStatus})` 
            : `News analysis for "${monitor.topic}" completed and sent! (${data.deliveryStatus})`
        });
        setCurrentReport({
          title: data.report.title,
          content: data.report.content,
          deliveryStatus: data.deliveryStatus
        });
        setActiveSubTab('instant');
        fetchMonitors();
        fetchReports();
      } else {
        throw new Error(data.error || 'Failed to run news analysis.');
      }
    } catch (err: any) {
      setStatusMessage({ type: 'error', text: err.message });
    } finally {
      setRunningMonitorId(null);
    }
  };

  const handlePublishSocial = async (reportContent: string, title: string) => {
    setIsPublishingSocial(true);
    try {
      const res = await fetch('/api/publish-report-social', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title,
          reportText: reportContent,
          channel: 'telegram'
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Помилка публікації.');
      alert(`✅ ${data.message}`);
    } catch (err: any) {
      alert(`⚠️ ${err.message || 'Не вдалося опублікувати звіт.'}`);
    } finally {
      setIsPublishingSocial(false);
    }
  };

  // Run instant search
  const handleRunInstantSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!instantTopic.trim()) return;

    setIsAnalyzing(true);
    setStatusMessage(null);
    setCurrentReport(null);

    try {
      const res = await fetch('/api/news-monitors/analyze-now', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: instantTopic.trim(),
          deliveryChannel,
          targetEmail: targetEmail.trim() || user?.email,
          telegramChatId: telegramChatId.trim() || null,
          sendNow: instantSendNow
        })
      });

      const data = await res.json();
      if (res.ok) {
        setCurrentReport({
          title: data.report.title,
          content: data.report.content,
          deliveryStatus: data.deliveryStatus
        });
        fetchReports();
      } else {
        throw new Error(data.error || 'Failed to run news search.');
      }
    } catch (err: any) {
      setStatusMessage({ type: 'error', text: err.message });
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Edit monitor helper
  const handleEditMonitor = (m: NewsMonitor) => {
    setEditingMonitorId(m.id);
    setTopic(m.topic);
    setFrequency(m.frequency);
    setExecutionTime(m.execution_time);
    setDeliveryChannel(m.delivery_channel);
    setTargetEmail(m.target_email || user?.email || '');
    setTelegramChatId(m.telegram_chat_id || '');
    setActiveSubTab('settings');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Copy report
  const handleCopyReport = (content: string) => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const [isDownloadingWord, setIsDownloadingWord] = useState(false);

  const handleDownloadWord = async (text: string, fileName: string) => {
    setIsDownloadingWord(true);
    try {
      const response = await fetch('/api/generate-docx', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text, fileName })
      });

      if (!response.ok) {
        throw new Error(isUk ? 'Помилка при генерації файлу Word.' : 'Failed to generate Word document.');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName.replace(/[^a-zA-Z0-9А-Яа-яЄєІіЇїҐґ_-]/g, '_')}.docx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      alert(err.message);
    } finally {
      setIsDownloadingWord(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
      {/* Top Header Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-slate-900/90 border border-slate-800 p-6 rounded-2xl backdrop-blur-xl shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-emerald-500/10 via-blue-500/5 to-transparent rounded-full blur-3xl -z-10 pointer-events-none" />
        
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={onGoHome || onBackToHub}
              className="p-2.5 bg-slate-800/80 hover:bg-slate-700/80 text-slate-300 rounded-xl transition-all border border-slate-700 flex items-center gap-2 text-sm font-medium font-mono"
            >
              <span>🏠 {isUk ? "На головну" : "Home"}</span>
            </button>
            <button
              onClick={onBackToHub}
              className="p-2.5 bg-slate-800/80 hover:bg-slate-700/80 text-slate-300 rounded-xl transition-all border border-slate-700 flex items-center gap-2 text-sm font-medium font-mono"
            >
              <ArrowLeft className="w-4 h-4 text-emerald-400" />
              <span>{isUk ? "← До Офісного Центру" : "← Back to Office Hub"}</span>
            </button>
          </div>
          
          <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl text-emerald-400">
            <Newspaper className="w-8 h-8" />
          </div>
          
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl md:text-2xl font-bold text-white tracking-tight">
                {isUk ? "Аналітик Новин & Дайджест" : "News Intelligence Agent"}
              </h1>
              <span className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-xs px-2.5 py-0.5 rounded-full font-semibold">
                Web Search 2026
              </span>
            </div>
            <p className="text-slate-400 text-xs md:text-sm mt-0.5">
              {isUk 
                ? "Автономний моніторинг інтернет-новин за вашою темою з доставкою на Email або в Telegram"
                : "Autonomous web news monitoring on custom topics delivered to Email or Telegram"}
            </p>
          </div>
        </div>

        {/* Sub-Navigation Tabs */}
        <div className="flex items-center gap-1.5 bg-slate-800/90 p-1.5 rounded-xl border border-slate-700/80 self-start md:self-auto">
          <button
            onClick={() => setActiveSubTab('settings')}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs md:text-sm font-medium transition-all ${
              activeSubTab === 'settings'
                ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-900/30'
                : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
            }`}
          >
            <Settings className="w-4 h-4" />
            <span>{isUk ? "Налаштування" : "Settings"}</span>
          </button>
          
          <button
            onClick={() => setActiveSubTab('instant')}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs md:text-sm font-medium transition-all ${
              activeSubTab === 'instant'
                ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-900/30'
                : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
            }`}
          >
            <Zap className="w-4 h-4" />
            <span>{isUk ? "Миттєвий Пошук" : "Instant Run"}</span>
          </button>

          <button
            onClick={() => setActiveSubTab('history')}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs md:text-sm font-medium transition-all ${
              activeSubTab === 'history'
                ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-900/30'
                : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
            }`}
          >
            <History className="w-4 h-4" />
            <span>{isUk ? "Історія Звітів" : "History"}</span>
            {reports.length > 0 && (
              <span className="bg-slate-700 text-slate-300 text-[10px] px-1.5 py-0.2 rounded-full">
                {reports.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Global Notification Banner */}
      {statusMessage && (
        <div className={`p-4 rounded-xl border flex items-center justify-between gap-3 animate-fadeIn ${
          statusMessage.type === 'success'
            ? 'bg-emerald-950/40 border-emerald-500/30 text-emerald-200'
            : 'bg-rose-950/40 border-rose-500/30 text-rose-200'
        }`}>
          <div className="flex items-center gap-3 text-sm font-medium">
            {statusMessage.type === 'success' ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 text-rose-400 shrink-0" />
            )}
            <span>{statusMessage.text}</span>
          </div>
          <button
            onClick={() => setStatusMessage(null)}
            className="text-xs text-slate-400 hover:text-white underline"
          >
            {isUk ? "Закрити" : "Dismiss"}
          </button>
        </div>
      )}

      {/* SUB-TAB 1: SETTINGS & ACTIVE MONITORS */}
      {activeSubTab === 'settings' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Form Side */}
          <div className="lg:col-span-7 bg-slate-900/80 border border-slate-800 p-6 rounded-2xl shadow-xl space-y-6">
            <div className="flex items-center justify-between border-b border-slate-800 pb-4">
              <div>
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-emerald-400" />
                  {editingMonitorId 
                    ? (isUk ? "Редагування моніторингу новин" : "Edit News Monitor")
                    : (isUk ? "Налаштування нового моніторингу" : "Configure New News Monitor")}
                </h2>
                <p className="text-xs text-slate-400 mt-0.5">
                  {isUk ? "Вкажіть тему, розклад та канал доставки звітів" : "Specify topic, schedule, and report delivery channel"}
                </p>
              </div>
              {editingMonitorId && (
                <button
                  onClick={() => {
                    setEditingMonitorId(null);
                    setTopic('');
                  }}
                  className="text-xs text-slate-400 hover:text-white underline"
                >
                  {isUk ? "Скасувати редагування" : "Cancel Edit"}
                </button>
              )}
            </div>

            <form onSubmit={handleSaveMonitor} className="space-y-5">
              {/* Topic Selection */}
              <div>
                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">
                  1. {isUk ? "Тема аналізу новин в Інтернеті *" : "News Analysis Topic *"}
                </label>
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder={isUk ? "Наприклад: Новини штучного інтелекту, Податкові зміни в Україні..." : "E.g., AI news updates, Real estate market in Kyiv..."}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-emerald-500 transition-all placeholder:text-slate-600"
                  required
                />
                
                {/* Topic Suggestions */}
                <div className="mt-3">
                  <span className="text-[11px] text-slate-400 block mb-1.5">
                    💡 {isUk ? "Популярні теми для вибору:" : "Suggested topics:"}
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {TOPIC_SUGGESTIONS.map((item, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setTopic(isUk ? item.uk : item.en)}
                        className="text-xs bg-slate-800/80 hover:bg-emerald-950/80 hover:border-emerald-500/40 text-slate-300 hover:text-emerald-300 px-3 py-1.5 rounded-lg border border-slate-700/60 transition-all"
                      >
                        {isUk ? item.uk : item.en}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Schedule Grid: Frequency & Time */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">
                    2. {isUk ? "Періодичність аналізу" : "Analysis Frequency"}
                  </label>
                  <select
                    value={frequency}
                    onChange={(e: any) => setFrequency(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-emerald-500 transition-all"
                  >
                    <option value="daily">{isUk ? "📅 Щодня (Daily)" : "📅 Daily"}</option>
                    <option value="hourly">{isUk ? "⏱️ Щогодини (Hourly)" : "⏱️ Hourly"}</option>
                    <option value="workdays">{isUk ? "💼 Робочі дні (Пн-Пт)" : "💼 Workdays (Mon-Fri)"}</option>
                    <option value="weekly">{isUk ? "📆 Раз на тиждень (Weekly)" : "📆 Weekly"}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">
                    3. {isUk ? "Час аналізу (за Києвом)" : "Execution Time (EET)"}
                  </label>
                  <div className="relative">
                    <input
                      type="time"
                      value={executionTime}
                      onChange={(e) => setExecutionTime(e.target.value)}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-emerald-500 transition-all"
                    />
                    <Clock className="w-4 h-4 text-slate-500 absolute right-3.5 top-3.5 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Delivery Destination */}
              <div className="pt-2">
                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">
                  4. {isUk ? "Куди надсилати звіт?" : "Delivery Destination"}
                </label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => setDeliveryChannel('telegram')}
                    className={`p-3 rounded-xl border flex flex-col items-center justify-center gap-1.5 transition-all text-xs font-medium ${
                      deliveryChannel === 'telegram'
                        ? 'bg-emerald-950/50 border-emerald-500 text-emerald-300 shadow-md shadow-emerald-950/50'
                        : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700'
                    }`}
                  >
                    <MessageSquare className="w-5 h-5 text-sky-400" />
                    <span>💬 Telegram</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setDeliveryChannel('email')}
                    className={`p-3 rounded-xl border flex flex-col items-center justify-center gap-1.5 transition-all text-xs font-medium ${
                      deliveryChannel === 'email'
                        ? 'bg-emerald-950/50 border-emerald-500 text-emerald-300 shadow-md shadow-emerald-950/50'
                        : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700'
                    }`}
                  >
                    <Mail className="w-5 h-5 text-indigo-400" />
                    <span>📧 Email</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setDeliveryChannel('both')}
                    className={`p-3 rounded-xl border flex flex-col items-center justify-center gap-1.5 transition-all text-xs font-medium ${
                      deliveryChannel === 'both'
                        ? 'bg-emerald-950/50 border-emerald-500 text-emerald-300 shadow-md shadow-emerald-950/50'
                        : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700'
                    }`}
                  >
                    <Send className="w-5 h-5 text-amber-400" />
                    <span>📧 + 💬 {isUk ? "Обидва" : "Both"}</span>
                  </button>
                </div>
              </div>

              {/* Target Credentials */}
              <div className="space-y-3 bg-slate-950/60 p-4 rounded-xl border border-slate-800">
                {(deliveryChannel === 'email' || deliveryChannel === 'both') && (
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">
                      {isUk ? "Email для розсилки:" : "Target Email:"}
                    </label>
                    <input
                      type="email"
                      value={targetEmail}
                      onChange={(e) => setTargetEmail(e.target.value)}
                      placeholder="user@example.com"
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-xs focus:border-emerald-500 focus:outline-none"
                    />
                  </div>
                )}

                {(deliveryChannel === 'telegram' || deliveryChannel === 'both') && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs text-slate-400">
                        {isUk ? "Telegram Chat ID (необов'язково, за замовчуванням дефолтний бот):" : "Telegram Chat ID (optional):"}
                      </label>
                      <span className="text-[10px] text-sky-400 flex items-center gap-1">
                        <HelpCircle className="w-3 h-3" /> @userinfobot
                      </span>
                    </div>
                    <input
                      type="text"
                      value={telegramChatId}
                      onChange={(e) => setTelegramChatId(e.target.value)}
                      placeholder="1421638496"
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-xs focus:border-emerald-500 focus:outline-none"
                    />
                  </div>
                )}
              </div>

              {/* Save Submit Button */}
              <button
                type="submit"
                disabled={isSaving}
                className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-semibold py-3.5 px-6 rounded-xl transition-all shadow-lg shadow-emerald-950/50 flex items-center justify-center gap-2 disabled:opacity-50 text-sm"
              >
                {isSaving ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>{isUk ? "Збереження..." : "Saving..."}</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>
                      {editingMonitorId
                        ? (isUk ? "Зберегти зміни" : "Save Changes")
                        : (isUk ? "Активувати моніторинг новин 🚀" : "Activate News Monitor 🚀")}
                    </span>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Active Monitors List Side */}
          <div className="lg:col-span-5 space-y-4">
            <div className="bg-slate-900/80 border border-slate-800 p-5 rounded-2xl shadow-xl">
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-800">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <Clock className="w-4 h-4 text-emerald-400" />
                  {isUk ? "Активні завдання моніторингу" : "Active Scheduled Tasks"}
                </h3>
                <span className="text-xs bg-slate-800 text-slate-300 px-2 py-0.5 rounded-full font-medium">
                  {monitors.length}
                </span>
              </div>

              {isLoadingMonitors ? (
                <div className="py-8 text-center text-slate-500 text-xs flex items-center justify-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin text-emerald-400" />
                  <span>{isUk ? "Завантаження списку..." : "Loading tasks..."}</span>
                </div>
              ) : monitors.length === 0 ? (
                <div className="py-10 text-center text-slate-500 text-xs space-y-2">
                  <Newspaper className="w-8 h-8 mx-auto text-slate-600 stroke-1" />
                  <p>{isUk ? "У вас ще немає збережених завдань моніторингу." : "No scheduled monitors created yet."}</p>
                  <p className="text-[11px] text-slate-600">
                    {isUk ? "Заповніть форму ліворуч, щоб створити перше завдання." : "Fill the form on the left to set up your first monitor."}
                  </p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1">
                  {monitors.map((m) => (
                    <div
                      key={m.id}
                      className={`p-4 rounded-xl border transition-all space-y-2.5 ${
                        m.is_active
                          ? 'bg-slate-950/80 border-slate-800 hover:border-emerald-500/40'
                          : 'bg-slate-950/40 border-slate-800/60 opacity-60'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="text-sm font-semibold text-white line-clamp-2">
                          {m.topic}
                        </h4>
                        <button
                          onClick={() => handleToggleActive(m)}
                          className={`p-1.5 rounded-lg border text-xs flex items-center gap-1 transition-all ${
                            m.is_active
                              ? 'bg-emerald-950/60 border-emerald-500/40 text-emerald-400'
                              : 'bg-slate-800 border-slate-700 text-slate-400'
                          }`}
                          title={m.is_active ? (isUk ? "Пауза" : "Pause") : (isUk ? "Активувати" : "Activate")}
                        >
                          {m.is_active ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                        </button>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-400">
                        <span className="bg-slate-800 px-2 py-0.5 rounded-md text-slate-300">
                          ⏱️ {m.frequency === 'hourly' ? (isUk ? 'Щогодини' : 'Hourly') : m.frequency === 'workdays' ? (isUk ? 'Робочі дні' : 'Workdays') : m.frequency === 'weekly' ? (isUk ? 'Щотижня' : 'Weekly') : (isUk ? 'Щодня' : 'Daily')} ({m.execution_time})
                        </span>

                        <span className="bg-slate-800 px-2 py-0.5 rounded-md text-slate-300">
                          {m.delivery_channel === 'telegram' ? '💬 Telegram' : m.delivery_channel === 'email' ? '📧 Email' : '📧 + 💬 Both'}
                        </span>
                      </div>

                      {m.last_run_at && (
                        <div className="text-[10px] text-slate-500">
                          {isUk ? "Останній запуск:" : "Last run:"} {new Date(m.last_run_at).toLocaleString('uk-UA')}
                        </div>
                      )}

                      <div className="flex items-center justify-end gap-2 pt-1 border-t border-slate-800/80">
                        <button
                          onClick={() => handleRunMonitorNow(m)}
                          disabled={runningMonitorId === m.id}
                          className="text-xs bg-emerald-950/80 hover:bg-emerald-900 text-emerald-300 border border-emerald-500/30 px-2.5 py-1 rounded-lg transition-all flex items-center gap-1.5 disabled:opacity-50"
                        >
                          {runningMonitorId === m.id ? (
                            <>
                              <RefreshCw className="w-3 h-3 animate-spin" />
                              <span>{isUk ? "Аналіз..." : "Running..."}</span>
                            </>
                          ) : (
                            <>
                              <Play className="w-3 h-3" />
                              <span>{isUk ? "Запустити зараз" : "Run Now"}</span>
                            </>
                          )}
                        </button>

                        <button
                          onClick={() => handleEditMonitor(m)}
                          className="text-xs text-slate-400 hover:text-white px-2 py-1 underline"
                        >
                          {isUk ? "Редагувати" : "Edit"}
                        </button>

                        <button
                          onClick={() => handleDeleteMonitor(m.id)}
                          className="text-xs text-rose-400 hover:text-rose-300 p-1"
                          title={isUk ? "Видалити" : "Delete"}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* SUB-TAB 2: INSTANT SEARCH */}
      {activeSubTab === 'instant' && (
        <div className="space-y-6">
          <div className="bg-slate-900/80 border border-slate-800 p-6 rounded-2xl shadow-xl">
            <h2 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-400" />
              {isUk ? "Миттєвий аналіз новин в Інтернеті" : "Instant Web News Analysis"}
            </h2>
            <p className="text-xs text-slate-400 mb-4">
              {isUk ? "Згенеруйте актуальний дайджест новин за довільною темою прямо зараз" : "Generate a real-time news briefing on any topic immediately"}
            </p>

            <button
              type="button"
              onClick={() => {
                setInstantTopic(isUk ? "Нові гранти та підтримка бізнесу в Україні 2026" : "New Grants and business funding 2026");
              }}
              className="w-full mb-3 py-2.5 px-4 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 rounded-xl text-emerald-300 font-mono text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-2 shadow-2xs"
            >
              <Sparkles className="w-4 h-4 text-emerald-400 animate-pulse" />
              <span>{isUk ? "⚡ Спробувати на демо-прикладі в 1 клік (Аналіз новин про Гранти 2026)" : "⚡ Try 1-click Demo Sample (Grants News 2026)"}</span>
            </button>

            <form onSubmit={handleRunInstantSearch} className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  value={instantTopic}
                  onChange={(e) => setInstantTopic(e.target.value)}
                  placeholder={isUk ? "Введіть тему новин (наприклад: Енергетика України, AI агенти 2026...)" : "Enter topic (e.g. Artificial Intelligence 2026...)"}
                  className="flex-1 bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-emerald-500 transition-all"
                  required
                />
                
                <button
                  type="submit"
                  disabled={isAnalyzing}
                  className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-semibold px-6 py-3 rounded-xl transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 text-sm whitespace-nowrap"
                >
                  {isAnalyzing ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>{isUk ? "Сканування новин..." : "Scanning News..."}</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      <span>{isUk ? "Проаналізувати зараз" : "Analyze Now"}</span>
                    </>
                  )}
                </button>
              </div>

              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={instantSendNow}
                    onChange={(e) => setInstantSendNow(e.target.checked)}
                    className="rounded border-slate-700 bg-slate-950 text-emerald-500 focus:ring-emerald-500"
                  />
                  <span>
                    {isUk ? "Також відправити результат у обраний канал (Email / Telegram)" : "Also deliver result to target channel (Email / Telegram)"}
                  </span>
                </label>
              </div>
            </form>
          </div>

          {/* Analysis Progress */}
          {isAnalyzing && (
            <div className="bg-slate-900/80 border border-slate-800 p-8 rounded-2xl text-center space-y-4 animate-pulse">
              <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto text-emerald-400">
                <RefreshCw className="w-6 h-6 animate-spin" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-white">
                  {isUk ? "ШІ-Агент проводить пошук та аналіз новин..." : "AI Agent is scanning live news sources..."}
                </h3>
                <p className="text-xs text-slate-400 mt-1">
                  {isUk ? "Сканування веб-сторінок 2026, збір фактів, сортування за важливістю та верстка звіту." : "Crawling news websites, evaluating facts, sorting by priority."}
                </p>
              </div>
            </div>
          )}

          {/* Current Report Result Card */}
          {currentReport && !isAnalyzing && (
            <div className="bg-slate-900/90 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden animate-fadeIn">
              {/* Report Header Bar */}
              <div className="bg-slate-950/80 border-b border-slate-800 p-4 px-6 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-base font-bold text-white flex items-center gap-2">
                    <FileText className="w-4 h-4 text-emerald-400" />
                    {currentReport.title}
                  </h3>
                  {currentReport.deliveryStatus && (
                    <span className="text-[11px] text-emerald-400 mt-0.5 block">
                      ✅ {currentReport.deliveryStatus}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleDownloadWord(currentReport.content, currentReport.title)}
                    disabled={isDownloadingWord}
                    className="p-2 bg-emerald-950/80 hover:bg-emerald-900 text-emerald-300 rounded-lg text-xs flex items-center gap-1.5 border border-emerald-500/30 transition-all font-semibold disabled:opacity-50"
                  >
                    {isDownloadingWord ? (
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Download className="w-3.5 h-3.5" />
                    )}
                    <span>WORD (.DOCX)</span>
                  </button>

                  <button
                    onClick={() => handlePublishSocial(currentReport.content, currentReport.title)}
                    disabled={isPublishingSocial}
                    className="p-2 bg-indigo-950/80 hover:bg-indigo-900 text-indigo-300 rounded-lg text-xs flex items-center gap-1.5 border border-indigo-500/30 transition-all font-semibold cursor-pointer disabled:opacity-50"
                  >
                    {isPublishingSocial ? (
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Send className="w-3.5 h-3.5" />
                    )}
                    <span>{isUk ? "📡 Публікація в Соцмережі / Telegram" : "📡 Publish to Socials"}</span>
                  </button>

                  <button
                    onClick={() => handleCopyReport(currentReport.content)}
                    className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs flex items-center gap-1.5 border border-slate-700 transition-all"
                  >
                    {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                    <span>{copied ? (isUk ? "Скопійовано" : "Copied") : (isUk ? "Копіювати" : "Copy")}</span>
                  </button>
                </div>
              </div>

              {/* Report Body */}
              <div className="p-6 text-slate-200 text-sm leading-relaxed overflow-x-auto whitespace-pre-wrap font-sans">
                {currentReport.content}
              </div>
            </div>
          )}
        </div>
      )}

      {/* SUB-TAB 3: REPORT HISTORY */}
      {activeSubTab === 'history' && (
        <div className="space-y-4">
          <div className="bg-slate-900/80 border border-slate-800 p-5 rounded-2xl shadow-xl flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <History className="w-5 h-5 text-indigo-400" />
                {isUk ? "Історія створених звітів з новин" : "News Report History"}
              </h2>
              <p className="text-xs text-slate-400 mt-0.5">
                {isUk ? "Переглядайте збережені та надіслані дайджести" : "View generated and dispatched news briefings"}
              </p>
            </div>

            <button
              onClick={fetchReports}
              className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs flex items-center gap-1.5 border border-slate-700 transition-all"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span>{isUk ? "Оновити" : "Refresh"}</span>
            </button>
          </div>

          {isLoadingReports ? (
            <div className="py-12 text-center text-slate-500 text-xs flex items-center justify-center gap-2">
              <RefreshCw className="w-4 h-4 animate-spin text-emerald-400" />
              <span>{isUk ? "Завантаження історії..." : "Loading history..."}</span>
            </div>
          ) : reports.length === 0 ? (
            <div className="py-12 text-center text-slate-500 text-xs bg-slate-900/40 border border-slate-800 rounded-2xl">
              <Newspaper className="w-8 h-8 mx-auto text-slate-600 stroke-1 mb-2" />
              <p>{isUk ? "Історія звітів порожня." : "No report history found."}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {reports.map((rep) => (
                <div
                  key={rep.id}
                  className="bg-slate-900/80 border border-slate-800 hover:border-emerald-500/40 p-5 rounded-2xl transition-all space-y-3 flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center justify-between text-[11px] text-slate-400 mb-1.5">
                      <span className="bg-slate-800 text-slate-300 px-2 py-0.5 rounded-md font-medium">
                        📌 {rep.topic}
                      </span>
                      <span>{new Date(rep.sent_at).toLocaleString('uk-UA')}</span>
                    </div>

                    <h3 className="text-sm font-semibold text-white line-clamp-2">
                      {rep.report_title}
                    </h3>

                    <p className="text-xs text-slate-400 line-clamp-3 mt-2 font-mono bg-slate-950/60 p-2.5 rounded-lg border border-slate-800/80">
                      {rep.report_content.replace(/[#*`-]/g, '')}
                    </p>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-slate-800 text-xs font-medium">
                    <span className="text-[11px] text-emerald-400">
                      ✅ {rep.delivery_status}
                    </span>

                    <button
                      onClick={() => setSelectedHistoryReport(rep)}
                      className="bg-slate-800 hover:bg-slate-700 text-slate-200 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1 font-medium"
                    >
                      <ExternalLink className="w-3.5 h-3.5 text-emerald-400" />
                      <span>{isUk ? "Читати звіт" : "Read Full"}</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* History Report Reader Modal */}
      {selectedHistoryReport && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 w-full max-w-4xl max-h-[85vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-fadeIn">
            <div className="p-4 px-6 bg-slate-950 border-b border-slate-800 flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-white">
                  {selectedHistoryReport.report_title}
                </h3>
                <p className="text-xs text-slate-400">
                  {new Date(selectedHistoryReport.sent_at).toLocaleString('uk-UA')} | {selectedHistoryReport.delivery_status}
                </p>
              </div>

              <button
                onClick={() => setSelectedHistoryReport(null)}
                className="p-2 text-slate-400 hover:text-white rounded-lg bg-slate-800"
              >
                ✕
              </button>
            </div>

            <div className="p-6 overflow-y-auto text-slate-200 text-sm leading-relaxed whitespace-pre-wrap">
              {selectedHistoryReport.report_content}
            </div>

            <div className="p-4 px-6 bg-slate-950 border-t border-slate-800 flex flex-wrap justify-end gap-3">
              <button
                onClick={() => handleDownloadWord(selectedHistoryReport.report_content, selectedHistoryReport.report_title)}
                disabled={isDownloadingWord}
                className="px-4 py-2 bg-emerald-950/80 hover:bg-emerald-900 text-emerald-300 rounded-xl text-xs flex items-center gap-1.5 border border-emerald-500/30 font-semibold disabled:opacity-50"
              >
                {isDownloadingWord ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span>WORD (.DOCX)</span>
              </button>
              <button
                onClick={() => handleCopyReport(selectedHistoryReport.report_content)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-xs flex items-center gap-1.5 border border-slate-700"
              >
                {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                <span>{copied ? (isUk ? "Скопійовано" : "Copied") : (isUk ? "Копіювати" : "Copy")}</span>
              </button>
              <button
                onClick={() => setSelectedHistoryReport(null)}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-semibold"
              >
                {isUk ? "Закрити" : "Close"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
